import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import usePagination from './usePagination';
import {
    getChats,
    getMessages,
    fetchMessage,
    getContacts,
    searchContacts,
    markMessageAsSeen
} from 'services/chat';

/**
 * Hook: useChats
 * Lista de conversas (últimas mensagens por contato)
 */
export const useChats = (noPagination = false) => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const { isPending, isError, data } = useQuery({
        queryKey: ['chats', { paginationModel, noPagination }],
        queryFn: async () => {
            if (noPagination) {
                let allData = [];
                let pageToken = null;
                let hasNext = true;

                while (hasNext) {
                    const resp = await getChats({
                        pageLimit: 50,
                        pageToken
                    });
                    allData = [...allData, ...(resp?.data || [])];
                    const nextToken = resp?.headers?.['next-page-token'];
                    if (nextToken) pageToken = nextToken;
                    else hasNext = false;
                }
                return { data: allData };
            } else {
                return getChats({
                    pageLimit: paginationModel.pageSize,
                    pageToken: currentToken(paginationModel.page)
                });
            }
        }
    });

    useEffect(() => {
        if (!noPagination && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data, noPagination]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

/**
 *
 * Mensagens de uma conversa
 */
export const useMessages = (
    chatterUuid,
    noPagination = false,
    refetchInterval = 30000
) => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const { isPending, isError, data } = useQuery({
        queryKey: ['messages', { chatterUuid, paginationModel, noPagination }],
        queryFn: async () => {
            if (!chatterUuid) return { data: [] };

            if (noPagination) {
                let allData = [];
                let pageToken = null;
                let hasNext = true;

                while (hasNext) {
                    const resp = await getMessages({
                        chatter: chatterUuid,
                        pageLimit: 50,
                        pageToken
                    });
                    allData = [...allData, ...(resp?.data || [])];
                    const nextToken = resp?.headers?.['next-page-token'];
                    if (nextToken) pageToken = nextToken;
                    else hasNext = false;
                }
                return { data: allData };
            } else {
                return getMessages({
                    chatter: chatterUuid,
                    pageLimit: paginationModel.pageSize,
                    pageToken: currentToken(paginationModel.page)
                });
            }
        },
        enabled: Boolean(chatterUuid),
        refetchInterval
    });

    useEffect(() => {
        if (!noPagination && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data, noPagination]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

/**
 * Hook: useContacts
 */
export const useContacts = (noPagination = false, searchTerm = '') => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const { isPending, isError, data } = useQuery({
        queryKey: ['contacts', { paginationModel, noPagination, searchTerm }],
        queryFn: async () => {
            const method = searchTerm ? searchContacts : getContacts;
            const params = {
                pageLimit: paginationModel.pageSize,
                pageToken: currentToken(paginationModel.page)
            };

            if (noPagination) {
                let allData = [];
                let pageToken = null;
                let hasNext = true;
                while (hasNext) {
                    const resp = await method({
                        ...params,
                        query: searchTerm,
                        pageLimit: 50,
                        pageToken
                    });
                    allData = [...allData, ...(resp?.data || [])];
                    const nextToken = resp?.headers?.['next-page-token'];
                    if (nextToken) pageToken = nextToken;
                    else hasNext = false;
                }
                return { data: allData };
            } else {
                return method({
                    ...params,
                    query: searchTerm
                });
            }
        }
    });

    useEffect(() => {
        if (!noPagination && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data, noPagination]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

/**
 * Hook: useMessage
 * Procurar mensagem
 */
export const useMessage = (uuid, enabled = true) => {
    const { isPending, isError, data } = useQuery({
        queryKey: ['message', uuid],
        queryFn: () => fetchMessage(uuid),
        enabled: enabled && Boolean(uuid)
    });

    return {
        data: data?.data || null,
        isLoading: isPending,
        error: isError
    };
};

export const useMarkAsSeen = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (uuids) => {
            if (!Array.isArray(uuids)) uuids = [uuids];
            const filtered = uuids.filter(Boolean);
            if (!filtered.length) return;

            await Promise.all(filtered.map((id) => markMessageAsSeen(id)));
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['messages']);
            queryClient.invalidateQueries(['chats']);
        }
    });
};
